/**
 * Compute Grid — MechCorp Data Center's unlock (MINIGAME_PROPOSALS O9). The
 * captured racks run YOUR workloads now. Idle Intrusion Protocols yield
 * Compute; Compute buys CPU (max active attacks), Storage (stash cap), and
 * Lattice Conductance, which multiplicatively eases the booster stacking
 * penalty (evaluatorV2 BOOST_DECAY) and scales forever.
 *
 * Design call: the Grid only ever ENABLES. Wiring is never limited; the
 * stacking penalty exists from the player's second booster onward, and this
 * is the system that buys it back, asymptotically toward (never reaching)
 * full passthrough. P57 replaced the RAM wire cap with this; spent RAM
 * Compute was refunded in the v19 migration.
 */

export interface IntrusionDef {
  id: string;
  name: string;
  flavor: string;
  durationMs: number;
  /** Compute yielded on completion. */
  yields: number;
}

export const INTRUSIONS: IntrusionDef[] = [
  {
    id: 'int.recon',
    name: 'Recon Node',
    flavor: 'Knock politely on a firewall. Take notes. Take the notes server.',
    durationMs: 20 * 60_000,
    yields: 6,
  },
  {
    id: 'int.cache',
    name: 'Cache Farm',
    flavor: 'Someone left a warehouse of RAM running. Finders keepers.',
    durationMs: 90 * 60_000,
    yields: 32,
  },
  {
    id: 'int.mainframe',
    name: 'Mainframe',
    flavor: 'The big one. It asks for a password. You ask for its racks.',
    durationMs: 6 * 60 * 60_000,
    yields: 150,
  },
];

export function getIntrusion(id: string): IntrusionDef | undefined {
  return INTRUSIONS.find((i) => i.id === id);
}

/** Each Conductance rank removes this fraction of the REMAINING stacking
 *  penalty (multiplicative: penalty × (1−this)^ranks → never quite zero). */
export const COND_PER_RANK = 0.08;

/** What one rank of Memory Lattice cost before P57 (kept for the v19 refund). */
export const LEGACY_RAM_BASE_COST = 18;
export const LEGACY_RAM_COST_GROWTH = 1.5;

export interface GridUpgradeDef {
  id: 'cpu' | 'storage' | 'cond';
  name: string;
  flavor: string;
  effectLabel: string;
  /** What one rank adds to the resource (0 for Conductance; it eases the penalty instead). */
  amount: number;
  baseCost: number;
  costGrowth: number;
}

export const GRID_UPGRADES: GridUpgradeDef[] = [
  {
    id: 'cpu',
    name: 'Core Allocator',
    flavor: 'One more thread for one more gun.',
    effectLabel: '+1 CPU (max active attacks)',
    amount: 1,
    baseCost: 30,
    costGrowth: 1.7,
  },
  {
    id: 'cond',
    name: 'Lattice Conductance',
    flavor: 'Better superconductors between your boosters. The stacking penalty hates this.',
    effectLabel: `-${Math.round(COND_PER_RANK * 100)}% remaining stacking penalty`,
    amount: 0,
    baseCost: 18,
    costGrowth: 1.5,
  },
  {
    id: 'storage',
    name: 'Cold Storage',
    flavor: 'A rack of drives that smell faintly of victory.',
    effectLabel: '+10 Storage (stash cap)',
    amount: 10,
    baseCost: 12,
    costGrowth: 1.5,
  },
];

export function getGridUpgrade(id: string): GridUpgradeDef | undefined {
  return GRID_UPGRADES.find((u) => u.id === id);
}
